import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LocaleProvider } from "./context/LocaleContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";
import { BRANDING } from "./config/branding";
import AutoUpdate from "./components/AutoUpdate";
// PWAInstallPrompt removed - focusing on native mobile apps only
import TrialBanner from "./components/TrialBanner";
import SubscriptionPaywall from "./components/SubscriptionPaywall";
import DayOneHook from "./components/DayOneHook";
import TravelDetectionBanner from "./components/TravelDetectionBanner";

// Pages
import LandingPage from "./pages/LandingPage";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProfileSetup from "./pages/ProfileSetup";
import Home from "./pages/Home";
import YourSpace from "./pages/YourSpace";
import AlloraChat from "./pages/AlloraChat";
import SuggestedFeed from "./pages/SuggestedFeed";
import WorkMode from "./pages/WorkMode";
import LifeMode from "./pages/LifeMode";
import HealthMode from "./pages/HealthMode";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Settings from "./pages/Settings";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Badges from "./pages/Badges";
import InvestorDashboard from "./pages/InvestorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import JournalPage from "./pages/JournalPage";

import JourneyGate from './components/JourneyGate';

// Protected Route Component - requires auth AND proper journey flow
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme?.colors?.gradientBg || 'from-purple-50/80 via-pink-50/60 to-white'} flex items-center justify-center`}>
        <div className="text-center">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse"
            style={{ background: `linear-gradient(135deg, ${theme?.colors?.primary || '#9333EA'}, ${theme?.colors?.accent || '#EC4899'})` }}
          >
            <span className="text-2xl">{theme?.emojis?.heart || '💜'}</span>
          </div>
          <p className="text-gray-500">Loading {BRANDING.app.name}...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Journey Gate handles: Onboarding → Trial → Subscription
  return <JourneyGate>{children}</JourneyGate>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route
        path="/profile-setup"
        element={
          <ProtectedRoute>
            <ProfileSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <YourSpace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/allora-chat"
        element={
          <ProtectedRoute>
            <AlloraChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suggested-feed"
        element={
          <ProtectedRoute>
            <SuggestedFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/work"
        element={
          <ProtectedRoute>
            <WorkMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/life"
        element={
          <ProtectedRoute>
            <LifeMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <HealthMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            <Badges />
          </ProtectedRoute>
        }
      />
      <Route
        path="/investor-dashboard"
        element={
          <ProtectedRoute>
            <InvestorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <LocaleProvider>
              <ThemeProvider>
                <SubscriptionProvider>
                  <TrialBanner />
                  <TravelDetectionBanner />
                  <AppRoutes />
                  <DayOneHook />
                  <SubscriptionPaywall />
                  {/* PWAInstallPrompt removed - focusing on native mobile apps */}
                  <Toaster position="top-center" richColors />
                  <AutoUpdate />
                </SubscriptionProvider>
              </ThemeProvider>
            </LocaleProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
